import { Button } from '@crowdex/ui/components/button';
import { showToast } from '@crowdex/ui/components/toaster';
import { cn } from '@crowdex/ui/lib/utils';
import { Cloud, LoaderCircle, MessageCircleQuestion, ShieldQuestion } from 'lucide-react';
import { type HTMLAttributes, type ReactNode, useState } from 'react';
import type { ActivityEntry } from '@/components/mobile-shell/activity-model';
import type { NeedsYouAnswer, NeedsYouOutcome } from './use-needs-you';

/** How long ago something started waiting: `8 min`, `2 h`, `3 d`. */
function waitingAge(at: string | number, now = Date.now()): string {
  // Preview fixtures give their age in hours rather than a time.
  const time =
    typeof at === 'number' ? (at < 1_000_000_000_000 ? now - at * 3_600_000 : at) : Date.parse(at);
  if (!Number.isFinite(time)) return '';
  const minutes = Math.max(0, Math.floor((now - time) / 60_000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

/**
 * A conversation waiting on the user, answered in place: an agent's question with its answers
 * (and "Reply…", which opens the conversation), or an approval with Deny / Approve. Shared by
 * the new conversation (under its composer) and the phone's Activity.
 */
export function NeedsYouCard({
  entry,
  variant,
  onOpen,
  onAnswer,
  openProps,
  children,
}: {
  entry: ActivityEntry;
  variant: 'desktop' | 'mobile';
  onOpen: () => void;
  onAnswer: (answer: NeedsYouAnswer) => Promise<NeedsYouOutcome>;
  /** Extra handlers of the part that opens the conversation (a long press). */
  openProps?: HTMLAttributes<HTMLButtonElement>;
  children?: ReactNode;
}) {
  const [pending, setPending] = useState<string | null>(null);
  const { thread, waiting } = entry;
  const question = waiting?.kind === 'question';
  const Icon = question ? MessageCircleQuestion : ShieldQuestion;
  const age = waitingAge(waiting?.notification.createdAt ?? thread.updatedAt);
  const meta = [thread.project, age].filter(Boolean).join(' · ');
  const text = waiting?.text || 'The agent waits on you.';
  const mobile = variant === 'mobile';

  const answer = (key: string, given: NeedsYouAnswer) => {
    setPending(key);
    void onAnswer(given)
      .then((outcome) => {
        if (outcome === 'answered') return;
        showToast({
          title: 'This request is no longer waiting.',
          description: 'Open the conversation to see where it stands.',
          tone: 'warning',
        });
        onOpen();
      })
      .catch(() => showToast({ title: 'The answer was not sent.', tone: 'error' }))
      .finally(() => setPending(null));
  };
  const spinner = (key: string) =>
    pending === key ? <LoaderCircle className="size-4 animate-spin" /> : null;
  const pill = 'h-9 rounded-control px-4';

  return (
    <article
      aria-label={thread.title}
      className={cn(
        'relative flex flex-col gap-3 text-left',
        mobile
          ? 'rounded-card border bg-card p-4'
          : 'rounded-card border border-attention/45 bg-background px-4 py-3.5',
      )}
      data-needs-you={waiting?.kind ?? 'action'}
    >
      <button
        className="flex w-full min-w-0 items-start gap-2.5 text-left"
        onClick={onOpen}
        type="button"
        {...openProps}
      >
        {!mobile && <Icon aria-hidden className="mt-0.5 size-4 shrink-0 text-attention" />}
        <span className={cn('min-w-0 flex-1', !mobile && 'flex items-baseline gap-3')}>
          <span
            className={cn(
              'block truncate font-semibold',
              mobile ? 'type-card-title' : 'type-body flex-1',
            )}
          >
            {thread.title}
          </span>
          <span
            className={cn(
              'type-meta flex shrink-0 items-center gap-1 text-muted-foreground',
              mobile && 'mt-0.5',
            )}
          >
            {mobile && thread.runtime === 'cloud' && <Cloud aria-hidden className="size-3.5" />}
            {meta}
          </span>
        </span>
        {mobile && <Icon aria-hidden className="mt-0.5 size-5 shrink-0 text-attention" />}
      </button>
      {/* A command to approve shows as code; any other request (an automation's) in words. */}
      {question || waiting === null || (waiting.detail === null && waiting.approval === null) ? (
        <p
          className={cn(
            'type-body whitespace-pre-wrap text-foreground/90',
            mobile && waiting !== null && 'rounded-2xl bg-control px-3.5 py-3',
          )}
        >
          {text}
        </p>
      ) : (
        <div className="space-y-2">
          {mobile && <p className="type-body text-foreground/90">Wants to run</p>}
          <pre className="overflow-x-auto rounded-xl bg-code-surface px-3 py-2 type-meta font-mono text-foreground">
            {waiting.detail ?? text}
          </pre>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {waiting?.approval ? (
          <>
            <Button
              className={mobile ? 'h-11 flex-1 rounded-control' : pill}
              disabled={pending !== null}
              onClick={() => answer('deny', { type: 'approval', approved: false })}
              variant="outline"
            >
              {spinner('deny')}
              Deny
            </Button>
            <Button
              className={mobile ? 'h-11 flex-1 rounded-control' : pill}
              disabled={pending !== null}
              onClick={() => answer('approve', { type: 'approval', approved: true })}
            >
              {spinner('approve')}
              Approve
            </Button>
          </>
        ) : (
          <>
            {waiting?.question?.choices.map((choice, index) => (
              <Button
                className={cn(pill, mobile && 'h-11')}
                disabled={pending !== null}
                key={choice.value}
                onClick={() =>
                  answer(`choice:${choice.value}`, { type: 'choice', value: choice.value })
                }
                variant={mobile && index === 0 ? 'secondary' : 'outline'}
              >
                {spinner(`choice:${choice.value}`)}
                {choice.label}
              </Button>
            ))}
            <Button
              className={cn(pill, mobile && 'h-11')}
              disabled={pending !== null}
              onClick={onOpen}
              variant="outline"
            >
              {question ? 'Reply…' : 'Open'}
            </Button>
          </>
        )}
      </div>
      {children}
    </article>
  );
}
